import { FC, Fragment, useState, useEffect, useRef, Dispatch } from "react";
import {
  MapObjectCard,
  ObjectMetadataTags,
  PageLoader,
  types,
  utils
} from "@vilocnv/allsetra-core";

// Data
import { useAppSelector } from "hooks";
import { isEmpty } from "lodash";
import {
  areRideCoordinatesEqual,
  getBoundsZoomLevel,
  getCenterCords,
  getElementSize,
  getParsedMetaDataForSpeed,
  getSegmentColor,
  getSegmentTransitionPoints,
  getSegmentPathArrows,
  getValidRideMapPoints,
  getMixedRideDisplaySegments,
  getSegmentPolylinePath,
  IObjectRideWithSegments,
  isMixedRideWithSegments,
  RideMapPoint,
  shouldUseRideStartEndOnly,
  sliceLocationsForSegment
} from "app/data/helpers";
import { selectGeozonesState } from "app/data/selectors";
import { useTranslation } from "react-i18next";
import SatelliteModeIcon from "../common/SatelliteModeIcon";
import {
  RideMapContainer,
  RideNotAvailableText
} from "./ObjectRideGoogleMap.styles";
import GoogleMap from "../Map/v2/GoogleMap";
import { InfoWindowF, MarkerF, PolylineF } from "@react-google-maps/api";
import { useMapState } from "../Map/v2/GoogleMap/MapStateContext";
import ObjectRideGoogleMapMarker from "./ObjectRideGoogleMapMarker";
import MixedRideSegmentPopup from "./MixedRideSegmentPopup";

const GPS_DOT_MIN_ZOOM = 14;
const ARROW_MIN_ZOOM = 12;

const mapPoint = (x: number, y: number) =>
  typeof google?.maps?.Point === "function"
    ? new google.maps.Point(x, y)
    : ({ x, y } as google.maps.Point);

const mapSize = (width: number, height: number) =>
  typeof google?.maps?.Size === "function"
    ? new google.maps.Size(width, height)
    : ({ width, height } as google.maps.Size);

const arrowSpacingMeters = (zoom: number) => {
  if (zoom >= 16) return 50;
  if (zoom >= 14) return 110;
  if (zoom >= 12) return 240;
  return 400;
};

export interface ObjectRideGoogleMapProps {
  objectRideLocations: Array<types.IObjectRideLocation>;
  objectRide: IObjectRideWithSegments | null;
  selectedLocation: types.IObjectRideLocation | null;
  setSelectedLocation: Dispatch<any>;
  onLocationItemClick: (data: types.IObjectRideLocation) => void;
  selectedSegmentId?: string | null;
  resolvedAddresses?: any[];
  onSegmentSelect?: (segmentId: string) => void;
}

const ObjectRideGoogleMap: FC<ObjectRideGoogleMapProps> = ({
  objectRideLocations,
  objectRide,
  selectedLocation,
  setSelectedLocation,
  onLocationItemClick,
  selectedSegmentId,
  resolvedAddresses = [],
  onSegmentSelect
}) => {
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const { t } = useTranslation();
  const objectStartStopLocations = [
    {
      latitude: objectRide?.startLatitude,
      longitude: objectRide?.startLongitude,
      uniqueId: "0" + objectRide?.uniqueId,
      date: objectRide?.startDate,
      speed: objectRide?.averageSpeed
    },
    {
      latitude: objectRide?.endLatitude,
      longitude: objectRide?.endLongitude,
      uniqueId: "1" + objectRide?.uniqueId,
      date: objectRide?.endDate,
      speed: objectRide?.averageSpeed
    }
  ];
  const alarmContainerRef = useRef<HTMLDivElement>();
  const [isLoaded, setMapLoaded] = useState<boolean>(false);
  const [zoomLevel, setZoomLevel] = useState<number>(0);
  const { addressFromLatLng } = useAppSelector(selectGeozonesState);

  const {
    mapRef,
    loadError,
    onMapLoad,
    centerCoords,
    showSatelliteMode,
    setShowSatelliteMode
  } = useMapState();

  const isStartEndOnly = shouldUseRideStartEndOnly(
    objectRide,
    objectRideLocations
  );
  const sourceLocations: RideMapPoint[] = (
    isStartEndOnly ? objectStartStopLocations : objectRideLocations
  ) as RideMapPoint[];
  const markerLocations = getValidRideMapPoints(sourceLocations);

  const handleLocationItemClick = (item: types.IObjectRideLocation) => {
    onLocationItemClick(item);
    if (item) {
      //@ts-ignore
      const { latitude, longitude } = item;
      mapRef?.panTo({ lat: latitude, lng: longitude });
    }
  };

  //for first time only
  useEffect(() => {
    const validLocations = getValidRideMapPoints(sourceLocations);

    if (mapRef && validLocations.length) {
      const startPoint = validLocations[0];
      const endPoint = validLocations[validLocations.length - 1];

      if (
        isStartEndOnly &&
        (validLocations.length === 1 ||
          areRideCoordinatesEqual(startPoint, endPoint))
      ) {
        const center = {
          lat: startPoint.latitude,
          lng: startPoint.longitude
        };
        mapRef.panTo(center);
        mapRef.setZoom(15);
        setZoomLevel(15);
      } else if (isStartEndOnly) {
        const bounds = new google.maps.LatLngBounds();
        validLocations.forEach((item) =>
          bounds.extend({
            lat: item.latitude,
            lng: item.longitude
          })
        );
        mapRef.fitBounds(bounds, {
          top: 50,
          right: 50,
          bottom: 50,
          left: 50
        });
        setZoomLevel(mapRef.getZoom() || 9);
      } else {
        const zoomLevel = getBoundsZoomLevel(
          validLocations,
          getElementSize(alarmContainerRef.current)
        );
        setZoomLevel(zoomLevel || 9);
        mapRef.setZoom(zoomLevel || 9);

        const center = getCenterCords(validLocations, centerCoords);
        mapRef.panTo(center);
      }
    } else {
      setZoomLevel(9);
    }

    return () => {
      setSelectedLocation(null);
    };
  }, [objectRideLocations, mapRef, isStartEndOnly]);

  useEffect(() => {
    if (!mapRef) return undefined;
    const listener = mapRef.addListener("zoom_changed", () => {
      const nextZoom = mapRef.getZoom();
      if (typeof nextZoom === "number") {
        setZoomLevel(nextZoom);
      }
    });
    return () => {
      google.maps.event.removeListener(listener);
    };
  }, [mapRef]);

  useEffect(() => {
    if (zoomLevel) setMapLoaded(true);
  }, [zoomLevel]);

  useEffect(() => {
    if (selectedLocation) {
      //@ts-ignore
      const { latitude, longitude } = selectedLocation;
      mapRef?.panTo({ lat: latitude, lng: longitude });
    }
  }, [addressFromLatLng]);

  useEffect(() => {
    if (!selectedSegmentId || !mapRef || !isMixedRideWithSegments(objectRide)) {
      return;
    }

    const segment = objectRide.segments.find(
      (item) => item.uniqueId === selectedSegmentId
    );
    if (!segment) return;

    const valid = getValidRideMapPoints(
      sliceLocationsForSegment(objectRideLocations, segment).map((item) => ({
        latitude: item.latitude,
        longitude: item.longitude,
        uniqueId: item.uniqueId,
        date: item.date
      }))
    );
    if (valid.length < 2) return;

    const bounds = new google.maps.LatLngBounds();
    valid.forEach((item) =>
      bounds.extend({
        lat: item.latitude,
        lng: item.longitude
      })
    );
    if (bounds.isEmpty()) return;

    const northEast = bounds.getNorthEast();
    const southWest = bounds.getSouthWest();
    if (northEast.equals(southWest)) return;

    mapRef.fitBounds(bounds, {
      top: 50,
      right: 50,
      bottom: 50,
      left: 50
    });
  }, [selectedSegmentId, mapRef, objectRide, objectRideLocations]);

  const icons = {
    start: require("../../../assets/icons/common/startLocation.svg").default,
    stop: require("../../../assets/icons/common/endLocation.svg").default
  };

  const getIcon = (index: number, array: any) => {
    // Markers are built while GoogleMap still shows its loader, so the Maps
    // script may not be there yet; without it `new google.maps.Point` throws
    // and the error boundary replaces the whole ride page.
    const mapsReady = typeof google !== "undefined" && !!google.maps?.Point;
    if (!mapsReady) return undefined;

    if (index === 0) {
      return {
        url: icons.start,
        scaledSize: utils.getMapSize(32, 32),
        anchor: mapPoint(25, 15)
      };
    } else if (index === array.length - 1) {
      return {
        url: icons.stop,
        scaledSize: utils.getMapSize(32, 32),
        anchor: mapPoint(25, 25)
      };
    }
    return {
      path: google.maps.SymbolPath.CIRCLE,
      scale: 5,
      fillColor: "#FFFFFF",
      fillOpacity: 1,
      strokeWeight: 1,
      strokeColor: "#959EB2"
    };
  };

  const getFormattedDate = (date: string) =>
    date ? utils.formatDate({ date }) : "N/A";

  const renderMarkers = () => {
    const result = markerLocations;
    const transitions =
      isMixedRideWithSegments(objectRide) && !isStartEndOnly
        ? getSegmentTransitionPoints(
            getMixedRideDisplaySegments(objectRide),
            objectRideLocations
          )
        : [];
    const isTransitionPoint = (item: {
      latitude?: number | null;
      longitude?: number | null;
    }) =>
      transitions.some(
        (point) =>
          Math.abs(point.latitude - (item.latitude ?? 0)) < 0.00012 &&
          Math.abs(point.longitude - (item.longitude ?? 0)) < 0.00012
      );

    return result.map((item: any, i: number, array: any[]) => {
      const isDotMarker = i !== 0 && i !== result.length - 1;
      if (
        isDotMarker &&
        (zoomLevel < GPS_DOT_MIN_ZOOM || isTransitionPoint(item))
      ) {
        return null;
      }
      const endpointTitle = isStartEndOnly
        ? i === 0
          ? t("typography.startLocation")
          : t("typography.endLocation")
        : undefined;

      return (
        <Fragment key={i}>
          <ObjectRideGoogleMapMarker
            objectName={
              endpointTitle || objectRide?.object?.name || t("typography.na")
            }
            marker={item}
            icon={getIcon(i, array) as google.maps.Icon}
            isDotMarker={isDotMarker}
            title={endpointTitle}
            onClick={() => handleLocationItemClick(item)}
          >
            {selectedLocation?.uniqueId === item?.uniqueId && (
              <InfoWindowF
                position={{
                  lat: selectedLocation?.latitude || 0,
                  lng: selectedLocation?.longitude || 0
                }}
                onCloseClick={() => setSelectedLocation(null)}
              >
                <MapObjectCard
                  label={objectRide?.object?.name || t("typography.na")}
                  id={objectRide?.object?.aNumber || t("typography.na")}
                  location={{
                    isLoading: false,
                    address: addressFromLatLng,
                    latitude: selectedLocation?.latitude as number,
                    longitude: selectedLocation?.longitude as number
                  }}
                  time={getFormattedDate(item?.date)}
                  translator={t}
                >
                  <ObjectMetadataTags
                    translator={t}
                    object={getParsedMetaDataForSpeed(item?.speed)}
                    direction={"row"}
                  />
                </MapObjectCard>
              </InfoWindowF>
            )}
          </ObjectRideGoogleMapMarker>
        </Fragment>
      );
    });
  };

  const renderPolylinePair = (
    path: Array<{ lat: number; lng: number }>,
    mainColor: string,
    highlighted: boolean,
    key: string,
    showArrows = false
  ) => {
    const hasSelection = Boolean(selectedSegmentId);
    const opacity = !hasSelection || highlighted ? 1 : 0.45;
    const zBoost = highlighted ? 4 : 0;
    const darkenedColor = utils.darkenColor(mainColor, 20);
    const arrows =
      showArrows && zoomLevel >= ARROW_MIN_ZOOM
        ? getSegmentPathArrows(path, key, arrowSpacingMeters(zoomLevel))
        : [];

    return (
      <Fragment key={key}>
        <PolylineF
          path={path}
          options={{
            strokeColor: darkenedColor,
            strokeWeight: 16,
            zIndex: 1 + zBoost,
            strokeOpacity: opacity
          }}
        />
        <PolylineF
          path={path}
          options={{
            strokeColor: mainColor,
            strokeWeight: 10,
            zIndex: 2 + zBoost,
            strokeOpacity: opacity
          }}
        />
        {arrows.map((arrow) => (
          <MarkerF
            key={arrow.uniqueId}
            position={{ lat: arrow.latitude, lng: arrow.longitude }}
            clickable={false}
            zIndex={5 + zBoost}
            icon={{
              path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
              scale: 3,
              fillColor: "#FFFFFF",
              fillOpacity: opacity,
              strokeColor: "#FFFFFF",
              strokeWeight: 1,
              strokeOpacity: opacity,
              rotation: arrow.heading
            }}
          />
        ))}
      </Fragment>
    );
  };

  const renderPolylineF = () => {
    if (isEmpty(objectRideLocations)) return null;

    if (isMixedRideWithSegments(objectRide) && !isStartEndOnly) {
      const displaySegments = getMixedRideDisplaySegments(objectRide);
      const visibleSegments = displaySegments.filter(
        (segment) => !segment.isPrivacyHidden
      );
      return (
        <Fragment>
          {visibleSegments.map((segment) => {
            const path = getSegmentPolylinePath(
              objectRideLocations,
              segment,
              displaySegments
            );
            if (path.length < 2) return null;

            return renderPolylinePair(
              path,
              getSegmentColor(segment.mode),
              selectedSegmentId === segment.uniqueId,
              segment.uniqueId,
              true
            );
          })}
        </Fragment>
      );
    }

    const path = objectRideLocations.map((item) => ({
      lat: item.latitude ?? 0,
      lng: item.longitude ?? 0
    }));

    return renderPolylinePair(path, "#498feb", true, "ride");
  };

  const renderTransitionMarkers = () => {
    if (!isMixedRideWithSegments(objectRide) || isStartEndOnly) {
      return null;
    }

    return getSegmentTransitionPoints(
      getMixedRideDisplaySegments(objectRide),
      objectRideLocations
    ).map((point) => {
      const fromColor = getSegmentColor(point.fromMode);
      const toColor = getSegmentColor(point.toMode);
      const rotation = point.heading - 90;
      const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32"><g transform="rotate(${rotation} 16 16)"><circle cx="16" cy="16" r="15" fill="#FFFFFF"/><path d="M16 4 A12 12 0 0 0 16 28 Z" fill="${fromColor}"/><path d="M16 4 A12 12 0 0 1 16 28 Z" fill="${toColor}"/><circle cx="16" cy="16" r="7.5" fill="#FFFFFF"/></g></svg>`;

      return (
        <MarkerF
          key={point.uniqueId}
          position={{ lat: point.latitude, lng: point.longitude }}
          options={{
            clickable: false,
            optimized: false,
            zIndex: 12,
            icon: {
              url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
              size: mapSize(32, 32),
              scaledSize: mapSize(32, 32),
              anchor: mapPoint(16, 16)
            }
          }}
        />
      );
    });
  };

  if (loadError) {
    return <div>Map cannot be loaded right now, sorry.</div>;
  }

  if (
    !objectRideLocations?.length &&
    !objectRide?.startLatitude &&
    !objectRide?.startLongitude &&
    !objectRide?.endLatitude &&
    !objectRide?.endLongitude
  ) {
    return (
      <RideNotAvailableText>
        {t("descriptions.rideDataNotAvialble")}
      </RideNotAvailableText>
    );
  }

  const selectedSegment =
    isMixedRideWithSegments(objectRide) &&
    selectedSegmentId &&
    !selectedLocation
      ? objectRide.segments.find((item) => item.uniqueId === selectedSegmentId)
      : undefined;

  return (
    <Fragment>
      {isLoaded ? (
        <RideMapContainer ref={alarmContainerRef}>
          <SatelliteModeIcon
            t={t}
            showSatelliteMode={showSatelliteMode}
            setShowSatelliteMode={setShowSatelliteMode}
          />
          <GoogleMap>
            {renderMarkers()}
            {renderPolylineF()}
            {renderTransitionMarkers()}
            {selectedSegment && objectRide ? (
              <MixedRideSegmentPopup
                ride={objectRide}
                segment={selectedSegment}
                locations={objectRideLocations}
                resolvedAddresses={resolvedAddresses}
                onClose={() => onSegmentSelect?.(selectedSegment.uniqueId)}
              />
            ) : null}
          </GoogleMap>
        </RideMapContainer>
      ) : (
        <PageLoader />
      )}
    </Fragment>
  );
};

export default ObjectRideGoogleMap;
